import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardCheck, ExternalLink, Plus, Target, CalendarClock, Users2, HeartPulse } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/common/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

const EDIT_ROLES = ["hah_physician", "physician", "admin"];

export default function CarePlanPatientTab({ patientId, readOnly = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = !readOnly && EDIT_ROLES.includes(user?.role_code);
  const [plan, setPlan] = useState(undefined); // undefined=loading, null=none
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get(`/care-plans/patient/${patientId}`); setPlan(r.data.care_plan); }
    catch (e) { toast.error(formatApiError(e)); setPlan(null); }
  }, [patientId]);
  useEffect(() => { load(); }, [load]);

  const createPlan = async () => {
    setBusy(true);
    try { const r = await api.post(`/care-plans/ensure/${patientId}`); navigate(`/care-plans/${r.data.id}`); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (plan === undefined) return <div className="py-8 text-sm text-[#6B7280]" data-testid="cpt-loading">Loading care plan…</div>;

  if (!plan) {
    return (
      <div className="hah-card p-8 text-center" data-testid="cpt-empty">
        <ClipboardCheck className="w-10 h-10 text-[#CBD5E1] mx-auto mb-2" />
        <p className="text-sm text-[#6B7280] mb-3">No care plan exists for this patient yet.</p>
        {canEdit && <Button onClick={createPlan} disabled={busy} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="cpt-create"><Plus className="w-4 h-4 mr-1.5" />{busy ? "Creating…" : "Create care plan"}</Button>}
      </div>
    );
  }

  const c = plan.content || {};
  const prog = plan.progress || {};
  const vp = c.visit_plan || {};
  const team = plan.care_team_resolved || {};

  return (
    <div className="space-y-4" data-testid="care-plan-patient-tab">
      <div className="hah-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-[#1F2937]">Care Plan</h3>
              <StatusBadge status={plan.status} />
              {plan.version > 0 && <span className="text-xs text-[#6B7280]">v{plan.version}</span>}
            </div>
            <div className="text-xs text-[#6B7280] mt-0.5">
              {plan.status === "published" ? `Published by ${plan.published_by_name || "—"}` : "Not yet published"}
              {plan.episode_ref ? ` · ${plan.episode_ref}` : ""}
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate(`/care-plans/${plan.id}`)} data-testid="cpt-open">
            <ExternalLink className="w-4 h-4 mr-1.5" />{canEdit && plan.status !== "published" ? "Open & complete" : "Open full plan"}
          </Button>
        </div>
      </div>

      {plan.status === "published" && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="hah-card p-5" data-testid="cpt-goals">
            <h4 className="text-sm font-semibold text-[#1F2937] flex items-center gap-2 mb-3"><Target className="w-4 h-4 text-[#0B7E8C]" />Care Goals</h4>
            {(c.goals || []).length === 0 ? <p className="text-sm text-[#94A3B8] italic">No goals.</p> : (c.goals || []).map((g, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 border-t border-[#F1F5F9] first:border-t-0">
                <span className="text-sm text-[#1F2937]">{g.goal}{g.target ? ` — ${g.target}` : ""}</span>
                <StatusBadge status={g.status} />
              </div>
            ))}
          </div>
          <div className="hah-card p-5" data-testid="cpt-discharge">
            <h4 className="text-sm font-semibold text-[#1F2937] flex items-center gap-2 mb-1"><ClipboardCheck className="w-4 h-4 text-[#0B7E8C]" />Discharge Criteria</h4>
            <p className="text-xs text-[#6B7280] mb-2">{prog.discharge_criteria_met}/{prog.discharge_criteria_total} met</p>
            {(c.discharge_criteria || []).map((cr, i) => (
              <div key={i} className="flex items-center gap-2 py-1">
                <span className={`w-2 h-2 rounded-full ${cr.met ? "bg-[#059669]" : "bg-[#CBD5E1]"}`} />
                <span className={`text-sm ${cr.met ? "text-[#059669] line-through" : "text-[#1F2937]"}`}>{cr.criterion}</span>
              </div>
            ))}
          </div>
          <div className="hah-card p-5" data-testid="cpt-visit">
            <h4 className="text-sm font-semibold text-[#1F2937] flex items-center gap-2 mb-3"><CalendarClock className="w-4 h-4 text-[#0B7E8C]" />Visit Plan</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="text-[#6B7280]">Physician/week</div><div className="text-[#1F2937]">{vp.physician_visits_per_week ?? "—"}</div>
              <div className="text-[#6B7280]">Nurse/day</div><div className="text-[#1F2937]">{vp.nurse_visits_per_day ?? "—"}</div>
              <div className="text-[#6B7280]">Teleconsults/week</div><div className="text-[#1F2937]">{vp.teleconsults_per_week ?? "—"}</div>
              <div className="text-[#6B7280]">Exp. discharge</div><div className="text-[#1F2937]">{vp.expected_discharge_date || "—"}</div>
            </div>
          </div>
          <div className="hah-card p-5" data-testid="cpt-team">
            <h4 className="text-sm font-semibold text-[#1F2937] flex items-center gap-2 mb-3"><Users2 className="w-4 h-4 text-[#0B7E8C]" />Care Team</h4>
            <div className="text-sm"><span className="text-[#6B7280]">Physicians: </span>{(team.physicians && team.physicians.length ? team.physicians.map((p) => p.name).filter(Boolean).join(", ") : team.physician?.name) || "—"}</div>
            <div className="text-sm mt-1"><span className="text-[#6B7280]">Nurses: </span>{(team.nurses || []).map((n) => n.name).filter(Boolean).join(", ") || "—"}</div>
            {c.monitoring?.rpm_enroll && <div className="text-xs text-[#0B7E8C] mt-2 inline-flex items-center gap-1"><HeartPulse className="w-3.5 h-3.5" />RPM monitoring enabled</div>}
          </div>
        </div>
      )}
    </div>
  );
}
